import React from "react";
import { Skeleton, useMediaQuery } from "@mui/material";
import LoadingBook from "../LoadingBook";
import { useSearchParams } from "react-router-dom";
import StudioArtworkLoading from "./StudioArtworkLoading";
import StudioLoadError from "./StudioLoadError";
import NavigationMenuButton from "../common/NavigationMenuButton";
import type { StudioPreferences } from "./useStudioPreferences";
import { STAGES } from "./studioModel";
import "./studio.css";

/** Keep the workspace geometry in place while its account or story loads. */
export default function StudioEmptyWorkspace({
  panels,
  connectionError,
  label = "Loading story workspace",
  onOpenNavigation,
  children,
  sidebar,
}: {
  panels: Pick<StudioPreferences["panels"], "projects" | "stepsExpanded">;
  connectionError?: { onRetry: () => void; retrying: boolean };
  label?: string;
  onOpenNavigation?: () => void;
  children?: React.ReactNode;
  sidebar?: React.ReactNode;
}) {
  const offline = !!connectionError;
  const desktop = useMediaQuery("(min-width:1200px)");
  const roomy = useMediaQuery("(min-width:1600px)");
  const wide = useMediaQuery("(min-width:1800px)");
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const expanded = desktop && (panels.stepsExpanded ?? roomy);
  const animation = offline || reducedMotion ? false : "pulse";
  const [params] = useSearchParams();
  const loadingStoryboard = !offline && params.get('section') === 'artwork' && params.get('view') !== 'assets'
    && !!(params.get('localChapterId') || params.get('storyId') || params.get('chapterId'));
  return (
    <div className="studio studio-empty-workspace" style={{ "--studio-projects-width": `${wide ? 304 : 280}px` } as React.CSSProperties}>
      <title>Fable Gambit</title>
      <meta name="robots" content="noindex, nofollow" />
      <div className="studio-body">
        {desktop && panels.projects && <aside className="studio-projects-shell open" aria-hidden={sidebar ? undefined : true}>
          {sidebar || <StudioSidebarLoading animation={animation} />}
        </aside>}
        <aside className={`studio-progress-shell${expanded ? " expanded" : ""}`} aria-hidden="true">
          <div className={`studio-progress-content${expanded ? " expanded" : ""}`}>
            <div className="studio-progress-heading"><Skeleton animation={animation} variant="rounded" width={expanded ? 120 : 28} height={24} /></div>
            <div className="studio-steps-loading">
              {STAGES.map(stage => <div className="studio-loading-row" key={stage}>
                <Skeleton animation={animation} variant="circular" width="var(--studio-step-size)" height="var(--studio-step-size)" />
                {expanded && <div className="studio-step-loading-copy">
                  <Skeleton animation={animation} variant="rounded" width="85%" height={16} />
                  <Skeleton animation={animation} variant="rounded" width="60%" height={12} />
                </div>}
              </div>)}
            </div>
          </div>
        </aside>
        <div className="studio-main">
          <header className="studio-header" aria-label="Story workspace">
            <div className="studio-header-start" aria-hidden="true"><Skeleton animation={animation} variant="rounded" width={140} height={24} /></div>
            <div className="studio-header-end">
              {onOpenNavigation && <NavigationMenuButton onClick={onOpenNavigation} />}
            </div>
          </header>
          {loadingStoryboard ? <div className="studio-preview-scroll"><div className="studio-preview-column"><StudioArtworkLoading /></div></div>
          : <div className="studio-workspace-loading" role={offline ? undefined : "status"} aria-label={offline ? undefined : label} aria-busy={!offline}>
            {connectionError ? <StudioLoadError variant="connection" {...connectionError} /> : <LoadingBook />}
          </div>}
          {children}
        </div>
      </div>
    </div>
  );
}

export function StudioSidebarLoading({ animation = "pulse" }: { animation?: "pulse" | false }) {
  return (
          <div className="studio-project-loading">
            <Skeleton animation={animation} variant="rounded" width="45%" height={24} />
            <Skeleton animation={animation} variant="rounded" sx={{ height: 38, "@media (pointer: coarse)": { height: 44 } }} />
            <Skeleton animation={animation} variant="rounded" sx={{ height: 38, "@media (pointer: coarse)": { height: 44 } }} />
            <div className="studio-project-loading-rows">
              {[80, 65, 90, 72].map((width, index) => <div className="studio-loading-row" key={index}>
                <Skeleton animation={animation} variant="rounded" width={22} height={22} />
                <Skeleton animation={animation} variant="rounded" width={`${width}%`} height={16} />
              </div>)}
            </div>
          </div>
  );
}
